import { action, computed, makeObservable, observable } from "mobx";
import { getFromCookieAsBoolean, setInCookie } from "../../util/cookies/cookie";
import type { IUserSessionStore } from "../user/UserSessionStore";
import { Pow3AccessRequestType } from "../../network/contracts/Pow3AccessRequestType";

export interface ICookieSettingsStore {
    showCookieBanner: boolean;
    acceptedAllCookies: boolean;
    acceptedNecessaryCookies: boolean;
    show(): void;
    acceptAll(): void;
    acceptNecessary(): void;
    close(): void;
}

export class CookieSettingsStore implements ICookieSettingsStore {
    private static readonly COOKIE_ACCEPTED_ALL_COOKIES = "Dobco.AcceptedAllCookies";
    private static readonly COOKIE_ACCEPTED_NECESSARY_COOKIES = "Dobco.AcceptedNecessaryCookies";
    private static readonly COOKIE_DAYS = 90;

    @observable
    public acceptedAllCookies: boolean = getFromCookieAsBoolean(CookieSettingsStore.COOKIE_ACCEPTED_ALL_COOKIES) ?? false;

    @observable
    public acceptedNecessaryCookies: boolean = getFromCookieAsBoolean(CookieSettingsStore.COOKIE_ACCEPTED_NECESSARY_COOKIES) ?? false;

    @observable
    public manualShowCookieBanner = false;

    constructor(private readonly userSessionStore: IUserSessionStore) {
        makeObservable(this);
    }

    public show(): void {
        this.setManualShowCookieBanner(true);
    }

    public close(): void {
        //Nothing chosen yet -> auto choose accepted necessary
        if (!this.acceptedAllCookies && !this.acceptedNecessaryCookies) {
            this.setAcceptedNecessaryCookies(true);
        }
        this.setManualShowCookieBanner(false);
    }

    @computed
    public get showCookieBanner() {
        return this.autoShowCookieBanner || this.manualShowCookieBanner;
    }

    @computed
    private get autoShowCookieBanner() {
        const { pow3AccessRequestType } = this.userSessionStore;

        //Never show cookie banner automatically when logged in with EMR or COZO because of reasons (annoying for users mostly)
        if (pow3AccessRequestType === Pow3AccessRequestType.Emr || pow3AccessRequestType === Pow3AccessRequestType.Cozo) {
            return false;
        }

        //Nothing accepted yet -> Show cookie banner
        return !this.acceptedAllCookies && !this.acceptedNecessaryCookies;
    }

    @action
    private setManualShowCookieBanner(value: boolean) {
        this.manualShowCookieBanner = value;
    }

    @action
    public setAcceptedAllCookies(value: boolean) {
        this.acceptedAllCookies = value;
        if (value) {
            setInCookie(CookieSettingsStore.COOKIE_ACCEPTED_ALL_COOKIES, true, CookieSettingsStore.COOKIE_DAYS);
        } else {
            setInCookie(CookieSettingsStore.COOKIE_ACCEPTED_ALL_COOKIES, false, -1);
        }
    }

    @action
    public setAcceptedNecessaryCookies(value: boolean) {
        this.acceptedNecessaryCookies = value;
        if (value) {
            setInCookie(CookieSettingsStore.COOKIE_ACCEPTED_NECESSARY_COOKIES, true, CookieSettingsStore.COOKIE_DAYS);
        } else {
            setInCookie(CookieSettingsStore.COOKIE_ACCEPTED_NECESSARY_COOKIES, false, -1);
        }
    }

    public acceptAll(): void {
        this.setAcceptedAllCookies(true);
        this.setAcceptedNecessaryCookies(false);
        this.setManualShowCookieBanner(false);
    }

    public acceptNecessary(): void {
        this.setAcceptedNecessaryCookies(true);
        this.setAcceptedAllCookies(false);
        this.setManualShowCookieBanner(false);
    }
}
